/**
 * فوریت درخواست وام — the one field on a loan request that says *when*.
 *
 * The member does not pick an adjective, they pick a deadline: the request
 * form offers «فوری(کمتر از 3 روز)» down to «پایین(بیشتر از 30 روز)»
 * (`pages/loans/add/utilities`). The word is only a label; the parenthesis is
 * the meaning. Both live here, because a manager reading «متوسط» on its own
 * has no scale to read it against.
 *
 * Only URGENT and HIGH carry colour. A queue where every row is tinted is a
 * queue where nothing stands out, and the whole point of showing فوریت in a
 * list is that «فوری» should be the thing the eye lands on.
 */

export type TDemandPriority = 'URGENT' | 'HIGH' | 'MID' | 'LOW'

interface IDemandPriorityMeta {
    /** the member's own word for it, as printed in the request form */
    label: string
    /** the deadline that word stands for, as a sentence about the member */
    need: string
    /** chip colours; the two quiet levels stay grey on purpose */
    chipClass: string
}

const META: Record<TDemandPriority, IDemandPriorityMeta> = {
    URGENT: {
        label: 'فوری',
        need: 'تا ۳ روز دیگر لازم دارد',
        chipClass: ' bg-danger-soft text-danger-on',
    },
    HIGH: {
        label: 'بالا',
        need: 'تا ۱۰ روز دیگر لازم دارد',
        chipClass: ' bg-warning-soft text-warning-on',
    },
    MID: {
        label: 'متوسط',
        need: 'تا ۳۰ روز دیگر لازم دارد',
        chipClass: ' bg-canvas text-text-secondary',
    },
    LOW: {
        label: 'پایین',
        need: 'عجله‌ای ندارد',
        chipClass: ' bg-canvas text-text-secondary',
    },
}

/**
 * Null for anything unrecognised — including the empty string the candidates
 * endpoint sends for a request filed before the field existed. A chip reading
 * «نامشخص» would be worse than no chip: it invents a fourth answer to a
 * question the member never saw.
 */
export const demandPriority = (
    value?: string | null
): IDemandPriorityMeta | null =>
    value && value in META ? META[value as TDemandPriority] : null

/**
 * «فوریت: فوری», for screen readers and titles.
 *
 * Deliberately WITHOUT `need`. The deadline the word stands for was counted
 * from the day the member filed — restating it next to the chip reads as a
 * claim about today, and on a request that has been in the queue for months
 * that claim is simply false. The word the member chose is kept; the sentence
 * that ages badly is not. `need` still describes the scale in the filter's
 * help text, where it is a definition rather than an assertion about anyone.
 */
export const demandPriorityTitle = (meta: IDemandPriorityMeta) =>
    `فوریت: ${meta.label}`

/**
 * The فوریت levels in the order the request form offers them, for a filter.
 *
 * Ordered urgent-first because that is the order a manager scans in, and the
 * empty value is «همه» rather than a fifth level — a filter's «off» is not a
 * choice the member could have made.
 */
export const DEMAND_PRIORITIES: TDemandPriority[] = [
    'URGENT',
    'HIGH',
    'MID',
    'LOW',
]
